import type { AccessState } from '@/types'
import { queryClient } from '../main'
import api, { type ApiRequestConfig } from '../services/api'
import authService from '../services/authService'
import { logError, logWarning } from '../utils/sentry'
import { createPersistedStore } from './baseStore'
import useBrandUserStore from './brandUserStore'
import useCampaignStore from './campaignStore'
import useInfluencerStore from './influencerStore'
import useRegistrationOptionsStore from './registrationOptionsStore'

type RegistrationStatus = 'incomplete' | 'completed' | null

interface BrandData {
  id: string
  registrationStep?: number
  firstName?: string
  lastName?: string
  email?: string
  jobPosition?: string
  phone?: string
  companyName?: string
  region?: string
  industries?: string[]
  annualBudget?: string
  annualBudgetCurrency?: string
  companySize?: string
  additionalInformation?: string
  brandLogoUrl?: string
  [key: string]: unknown
}

type AuthResult = { ok: true } | { ok: false }

interface AccessActions {
  setError: (error: string | null) => void
  setValue: (key: string, value: unknown) => void
  fetchBrandData: () => Promise<BrandData | null>
  checkRegistrationStatus: () => Promise<RegistrationStatus>
  getRouteByRegistrationStep: (registrationStep: number) => string | null
  login: (email: string, password: string) => Promise<AuthResult>
  logout: () => Promise<void>
  initialize: () => Promise<void>
  refreshTokens: () => Promise<boolean>
  resetPassword: (email: string) => Promise<number | null>
  verifyOTP: (email: string, otp: string) => Promise<number | null>
  verifyRegistrationOTP: (email: string, otp: string) => Promise<number>
  createNewPassword: (newPassword: string, confirmPassword: string) => Promise<number | null>
  setRegistrationInProgress: (inProgress: boolean) => void
}

const REQUIRED_FIELDS: string[] = [
  'jobPosition',
  'phone',
  'companyName',
  'region',
  'industries',
  'annualBudget',
  'annualBudgetCurrency',
  'companySize',
]

const initialState: AccessState = {
  error: null,
  loading: false,
  brandData: null,
  registrationStatus: null,
  initialized: false,
  user: null,
  isAuthenticated: false,
  isRegistrationInProgress: false,
}

const actions = (set: (state: Partial<AccessState>) => void, get: () => AccessState & AccessActions): AccessActions => ({
  setError: (error) => {
    if (error) {
      logError('Access store error:', { error })
    }
    set({ error })
  },

  setRegistrationInProgress: (inProgress: boolean) => {
    set({ isRegistrationInProgress: inProgress })
  },

  setValue: (key, value) => {
    set({
      [key]: value,
    })
  },

  getRouteByRegistrationStep: (registrationStep: number) => {
    switch (registrationStep) {
      case 1:
        return '/registration/enter-otp'
      case 2:
        return '/registration/profile'
      case 3:
        return '/registration/industries'
      case 4:
        return '/registration/business'
      case 5:
        return '/registration/upload-logo'
      case 6:
        return '/campaigns'
      default:
        return null
    }
  },

  fetchBrandData: async () => {
    try {
      const response = await api.get<BrandData>('/brands', { silentAuthCheck: true } as ApiRequestConfig)

      if (!response.data) {
        logError('API response missing data object')
        return null
      }

      const brandData = response.data

      if (!brandData.id) {
        logError('Brand data missing ID field', { brandData })
        return null
      }

      useBrandUserStore.getState().setValue('id', String(brandData.id))

      const transformedBrandData = {
        id: String(brandData.id),
        registrationStep: brandData.registrationStep,
        firstName: brandData.firstName || '',
        lastName: brandData.lastName || '',
        email: brandData.email || '',
        jobPosition: brandData.jobPosition || '',
        phoneNumber: brandData.phone || '',
        companyName: brandData.companyName || '',
        region: brandData.region || '',
        industries: brandData.industries || [],
        annualBudget: brandData.annualBudget || '',
        companySize: brandData.companySize || '',
        brandLogoUrl: brandData.brandLogoUrl || '',
        annualBudgetCurrency: brandData.annualBudgetCurrency ?? '',
      }

      set({ brandData: transformedBrandData })

      return brandData
    } catch (error: unknown) {
      logError('Failed to fetch brand data:', { error })
      console.error('API request error:', error)

      if (error instanceof Error && 'response' in error && typeof error.response === 'object' && error.response !== null) {
        if ('status' in error.response && error.response.status === 404) {
          return null
        }
      }

      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      throw new Error(`Failed to fetch brand data: ${errorMessage}`)
    }
  },

  checkRegistrationStatus: async () => {
    try {
      set({ loading: true })

      let brandData = get().brandData

      if (!brandData) {
        const rawData = await get().fetchBrandData()
        if (rawData) {
          brandData = {
            id: rawData.id,
            registrationStep: rawData.registrationStep,
            firstName: rawData.firstName || '',
            lastName: rawData.lastName || '',
            email: rawData.email || '',
            jobPosition: rawData.jobPosition || '',
            phoneNumber: rawData.phone || '',
            companyName: rawData.companyName || '',
            region: rawData.region || '',
            industries: rawData.industries || [],
            annualBudget: rawData.annualBudget || '',
            annualBudgetCurrency: rawData.annualBudgetCurrency ?? '',
            companySize: rawData.companySize || '',
            brandLogoUrl: rawData.brandLogoUrl || '',
          }
        }
      }

      if (brandData) {
        const isStepTwoComplete = REQUIRED_FIELDS.every((field) => {
          const fieldExists = field in brandData
          const fieldHasValue = brandData[field] !== null && brandData[field] !== undefined && brandData[field] !== ''

          return fieldExists && fieldHasValue
        })
        let registrationStatus: RegistrationStatus

        if (!isStepTwoComplete) {
          registrationStatus = 'incomplete'
        } else {
          registrationStatus = 'completed'
        }

        set({
          loading: false,
          registrationStatus,
          brandData,
        })
        return registrationStatus
      }

      set({
        loading: false,
        registrationStatus: 'incomplete',
        brandData: null,
      })
      return 'incomplete'
    } catch (error: unknown) {
      logError('Failed to check registration status:', { error })
      set({ loading: false })
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      throw new Error(`Failed to check registration status: ${errorMessage}`)
    }
  },

  login: async (email: string, password: string): Promise<AuthResult> => {
    set({ loading: true, error: null })

    try {
      await authService.login(email, password)
    } catch (loginError) {
      try {
        type RespShape = { data?: { data?: { password?: string | string[] } } }
        const maybeResp = (loginError as { response?: RespShape }).response
        const pwdField = maybeResp?.data?.data?.password
        const hasVerificationRequired =
          (typeof pwdField === 'string' && pwdField.includes('Verification required')) ||
          (Array.isArray(pwdField) && pwdField.some((m: unknown) => typeof m === 'string' && m.includes('Verification required')))

        if (hasVerificationRequired) {
          if (typeof window !== 'undefined' && window.location?.pathname !== '/registration/enter-otp') {
            window.location.replace('/registration/enter-otp')
          }
        }
      } catch (_) {
        // ignore shape parsing issues
      }

      logError('Login failed:', { error: loginError })

      set({
        loading: false,
        error: 'Login failed. Please check your credentials.',
      })

      throw loginError
    }

    set({
      loading: false,
      isAuthenticated: true,
    })

    try {
      await get().fetchBrandData()
      const brandData = get().brandData as Record<string, unknown> | null

      if (brandData && (brandData as { id?: string }).id) {
        const isStepTwoComplete = REQUIRED_FIELDS.every((field) => {
          const fieldExists = field in brandData
          const fieldHasValue =
            (brandData as Record<string, unknown>)[field] !== null &&
            (brandData as Record<string, unknown>)[field] !== undefined &&
            (brandData as Record<string, unknown>)[field] !== ''

          return fieldExists && fieldHasValue
        })
        const registrationStatus: RegistrationStatus = !isStepTwoComplete ? 'incomplete' : 'completed'
        set({
          registrationStatus,
          loading: false,
        })

        // Redirect based on registrationStep from backend
        const registrationStep = (brandData as { registrationStep?: number }).registrationStep
        if (typeof window !== 'undefined' && registrationStep) {
          const targetRoute = get().getRouteByRegistrationStep(registrationStep)
          if (targetRoute) {
            window.location.replace(targetRoute)
          }
        }
      }
    } catch (brandDataError) {
      logError('Failed to fetch brand data after login:', { error: brandDataError })
    }

    return { ok: true }
  },

  logout: async () => {
    try {
      set({ loading: true })

      await authService.logout()

      queryClient.clear()

      set({
        brandData: null,
        registrationStatus: null,
        error: null,
        loading: false,
        initialized: true,
        isAuthenticated: false,
        user: null,
      })

      useBrandUserStore.getState().reset()
      useCampaignStore.getState().reset()
      useInfluencerStore.getState().reset()
      useRegistrationOptionsStore.getState().reset()
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error while logging out'
      logError('Logout failed:', { error })
      set({ loading: false, error: errorMessage })
      throw new Error(`Logout failed: ${errorMessage}`)
    }
  },

  initialize: async () => {
    try {
      set({ loading: true })

      let brandData = null
      try {
        brandData = await get().fetchBrandData()
      } catch (fetchError: unknown) {
        logError('Error during auth initialization', { error: fetchError })
        set({ loading: false, initialized: true, isAuthenticated: false })
        return
      }

      if (!brandData?.id) {
        logWarning('Brand data missing ID after initialization')
        set({
          registrationStatus: 'incomplete',
          loading: false,
          initialized: true,
        })
        return
      }

      const isStepTwoComplete = REQUIRED_FIELDS.every((field) => {
        const fieldExists = field in brandData
        const fieldHasValue = brandData[field] !== null && brandData[field] !== undefined && brandData[field] !== ''

        return fieldExists && fieldHasValue
      })

      const registrationStatus: RegistrationStatus = !isStepTwoComplete ? 'incomplete' : 'completed'

      set({
        registrationStatus,
        loading: false,
        initialized: true,
        isAuthenticated: true,
      })
    } catch (error) {
      logError('Failed to initialize auth:', { error })
      set({
        loading: false,
        initialized: true,
        registrationStatus: null,
      })
    }
  },

  refreshTokens: async () => {
    try {
      await authService.refreshToken('')
      await get().checkRegistrationStatus()
      return true
    } catch (error) {
      logError('Token refresh failed:', { error })
      set({ isAuthenticated: false })
      return false
    }
  },

  resetPassword: async (email: string) => {
    try {
      return await authService.resetPassword(email)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error resetting password'
      logError('Password reset failed:', { error })
      throw new Error(`Password reset failed: ${errorMessage}`)
    }
  },

  verifyOTP: async (email: string, verificationCode: string) => {
    try {
      const response = await authService.verifyOTP(email, verificationCode)
      return typeof response === 'number' ? response : 200
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error verifying OTP'
      logError('OTP verification failed:', { error })
      throw new Error(`OTP verification failed: ${errorMessage}`)
    }
  },
  verifyRegistrationOTP: async (email: string, verificationCode: string) => {
    try {
      await authService.verifyRegistrationOTP(email, verificationCode)

      set({ isAuthenticated: true })

      await (async () => {
        try {
          await get().fetchBrandData()
        } catch (_e) {
          // non-fatal
        }
        try {
          await get().checkRegistrationStatus()
        } catch (_e) {
          // non-fatal
        }
      })()

      return 200
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error verifying registration OTP'
      logError('Registration OTP verification failed:', { error })
      throw new Error(`Registration OTP verification failed: ${errorMessage}`)
    }
  },
  createNewPassword: async (newPassword: string, confirmPassword: string) => {
    try {
      const response = await authService.createNewPassword(newPassword, confirmPassword)
      return typeof response === 'number' ? response : 200
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error creating new password'
      logError('New password creation failed:', { error })
      throw new Error(`New password creation failed: ${errorMessage}`)
    }
  },
})

const useAccessStore = createPersistedStore<AccessState, AccessActions>('access-store', initialState, actions)

export default useAccessStore
